import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  Pagination,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Paper,
  Menu,
  MenuItem,
  TextField,
  IconButton,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import SortIcon from '@mui/icons-material/Sort';
import { Book, Category, BookStats } from '../types';
import BookCard from './BookCard';
import { translations } from '../translations';
import SearchIcon from '@mui/icons-material/Search';

const DRAWER_WIDTH = 240;

const formatNumber = (num: number | string): string => {
  return Number(num).toLocaleString('en-US');
};

const BooksList = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [genres, setGenres] = useState<Category[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<BookStats>({
    totalBooks: 0,
    totalDownloads: 0,
    totalViews: 0,
  });
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<string>('download');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search') || '';

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await axios.get('/api/genres');
        setGenres(response.data.genres);
      } catch (error) {
        console.error('Error fetching genres:', error);
      }
    };
    fetchGenres();
  }, []);

  useEffect(() => {
    const fetchBooks = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '20',
          search: searchQuery,
          sortBy: sortBy,
          sortOrder: sortOrder
        });

        if (selectedGenres.length > 0) {
          selectedGenres.forEach(genre => {
            params.append('genre', genre.toString());
          });
        }

        const response = await axios.get(`/api/books?${params}`);
        setBooks(response.data.books);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error('Error fetching books:', error);
      }
      setLoading(false);
    };

    fetchBooks();
  }, [page, selectedGenres, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get('/api/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  const handleGenreToggle = (genreId: number) => {
    setSelectedGenres(prev =>
      prev.includes(genreId)
        ? prev.filter(id => id !== genreId)
        : [...prev, genreId]
    );
    setPage(1);
  };

  const handleBookClick = (bookId: number) => {
    navigate(`/books/${bookId}`);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (sort: string) => {
    if (sort === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sort);
      setSortOrder('asc');
    }
    handleSortClose();
    setPage(1); // Reset to first page when sorting changes
  };

  const handleAuthorClick = (authorId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering book click when clicking author
    navigate(`/authors/${authorId}`);
  };

  const handleSearch = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    
    const params = new URLSearchParams(location.search);
    params.set('search', searchInput);
    
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setPage(1);
  };

  const language = localStorage.getItem('language') || 'ku';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = language === 'ku';
  const t = isRtl ? translations.ku : translations.en;

  const drawer = (
    <Box sx={{ 
      width: DRAWER_WIDTH,
      p: 2,
      direction: isRtl ? 'rtl' : 'ltr',
      textAlign: isRtl ? 'right' : 'left'
    }}>
      <Typography variant="h6" gutterBottom>
        {t.bookStatistics}
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'white' }}>
            <Typography variant="h4" align="center">
              {formatNumber(stats.totalBooks)}
            </Typography>
            <Typography variant="subtitle2" align="center">
              {t.totalBooks}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'secondary.light', color: 'white' }}>
            <Typography variant="h4" align="center">
              {formatNumber(stats.totalDownloads)}
            </Typography>
            <Typography variant="subtitle2" align="center">
              {t.downloads}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
            <Typography variant="h4" align="center">
              {formatNumber(stats.totalViews)}
            </Typography>
            <Typography variant="subtitle2" align="center">
              {t.views}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        {t.genres}
      </Typography>
      <List>
        {genres.map((genre) => (
          <ListItem key={genre.id} disablePadding>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedGenres.includes(genre.id)}
                  onChange={() => handleGenreToggle(genre.id)}
                />
              }
              label={
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  width: '100%',
                  alignItems: 'center'
                }}>
                  <Typography>
                    {isRtl ? genre.name_ku : genre.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    ({formatNumber(genre.book_count)})
                  </Typography>
                </Box>
              }
              sx={{ 
                width: '100%',
                margin: 0,
                justifyContent: isRtl ? 'flex-end' : 'flex-start',
                '& .MuiTypography-root': {
                  textAlign: isRtl ? 'right' : 'left',
                  direction: isRtl ? 'rtl' : 'ltr'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const getSortLabel = (field: string, order: 'asc' | 'desc') => {
    if (sortBy === field) {
      return order === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      bgcolor: 'background.default',
      position: 'relative'
    }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={isMobile && mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: '56px', // Mobile header height
            height: 'calc(100% - 56px)',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            position: 'fixed',
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              top: 64,
              height: 'calc(100% - 64px)',
              bgcolor: 'background.paper',
              borderRight: isRtl ? '1px solid' : 'none',
              borderLeft: isRtl ? '1px solid' : 'none',
              borderColor: 'divider',
              position: 'fixed',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 3 },
          width: '100%',
          marginLeft: isRtl ? { sm: `${DRAWER_WIDTH}px` } : 0,
          marginRight: isRtl ? { sm: `${DRAWER_WIDTH}px` } : 0,
        }}
      >
        <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2 } }}>
          {/* Add Search Form */}
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              mb: 2,
              display: 'flex',
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t.searchBooks}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" edge="end">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          {/* Mobile Filter/Sort Controls */}
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' },
            gap: 1,
            mb: 2,
          }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleSortClick}
              startIcon={<SortIcon />}
              sx={{ flex: 1 }}
            >
              {t.sort}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ flex: 1 }}
            >
              {t.genres}
            </Button>
          </Box>

          {/* Desktop Sort Control */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            justifyContent: 'flex-end', 
            mb: 2
          }}>
            <Button
              onClick={handleSortClick}
              startIcon={<SortIcon />}
              variant="outlined"
            >
              {t.sort}
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid 
                container 
                spacing={{ xs: 1, sm: 3 }} // Reduced spacing on mobile
                direction={isRtl ? 'row-reverse' : 'row'}
              >
                {books?.map((book) => (
                  <Grid item key={book?.id} xs={6} sm={6} md={4} lg={3}>
                    <BookCard
                      book={book}
                      isRtl={isRtl}
                      onBookClick={handleBookClick}
                      onAuthorClick={handleAuthorClick}
                      formatNumber={formatNumber}
                    />
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: { xs: 2, sm: 4 }, 
                mb: { xs: 2, sm: 2 }
              }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size={isMobile ? "small" : "large"}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
              </Box>
            </>
          )}
        </Container>
      </Box>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRtl ? 'right' : 'left',
        }}
      >
        <MenuItem onClick={() => handleSortSelect('download')}>
          {t.byDownloads}{getSortLabel('download', sortOrder)}
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect('likes')}>
          {t.byLikes}{getSortLabel('likes', sortOrder)}
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect('viewed')}>
          {t.byViews}{getSortLabel('viewed', sortOrder)}
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect('title')}>
          {t.byTitle}{getSortLabel('title', sortOrder)}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BooksList; 